import { BRAND } from "constants/brand";
import "./home.scss";
import { useGetAnalyticsTimeSeries, useGetStatistics } from "hooks/useHome";
import { useMemo, useRef, useState } from "react";
import { Ring } from "@uiball/loaders";
import { Toast } from "primereact/toast";
import useCity from "hooks/useCity";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBurger,
    faCartShopping,
    faLocationDot,
    faMoneyBillTrendUp,
    faReceipt,
    faTrash,
    faTruckFast,
    faUser,
    faUsers,
} from "@fortawesome/free-solid-svg-icons";
import ChartComponent from "./ChartComponent";
import { useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { formatMonth } from "utils/dateFormat";
import DateTimeField from "components/Ui/DateTimeField/DateTimeField";
import { ORDER_STATUS_CHOICES } from "constants/orderStatus";

const formatStatValue = (value) => {
    if (typeof value !== "number") return value ?? "—";
    return value.toLocaleString();
};

const HomeStatCard = ({ tone, icon, value, label, hint }) => (
    <article className={`home-stat home-stat--${tone}`}>
        <div className="home-stat__icon">{icon}</div>
        <div className="home-stat__content">
            <span className="home-stat__value">{formatStatValue(value)}</span>
            <span className="home-stat__label">{label}</span>
            {hint ? <span className="home-stat__hint">{hint}</span> : null}
        </div>
    </article>
);

const Home = () => {
    const toast = useRef(null);
    const { cities } = useCity();
    const adminInfo = useSelector((state) => state.auth.userData);
    const [city, setCity] = useState(null);
    const [periodPreset, setPeriodPreset] = useState("12");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [orderStatus, setOrderStatus] = useState("all");
    const selectedCityName = useMemo(
        () => cities?.find((item) => String(item.id) === String(city))?.name,
        [cities, city],
    );
    const cityScopeLabel = adminInfo?.super_admin
        ? city
            ? selectedCityName || "المدينة المحددة"
            : "كل المدن"
        : "مدينتك";

    // Polled every 30s — see useGetStatistics. The page used to fetch once and
    // then never again, so the driver count and the order figures aged silently
    // for as long as the tab stayed open.
    const { data, loading } = useGetStatistics(city);
    const analyticsFilters = useMemo(() => {
        const custom = periodPreset === "custom";
        return {
            months: custom ? undefined : Number(periodPreset),
            startDate: custom ? customStartDate || undefined : undefined,
            endDate: custom ? customEndDate || undefined : undefined,
            status: orderStatus === "all" ? undefined : orderStatus,
            enabled:
                adminInfo?.role === "admin" &&
                (!custom || Boolean(customStartDate && customEndDate)),
        };
    }, [adminInfo?.role, customEndDate, customStartDate, orderStatus, periodPreset]);
    const { data: analyticsData, loading: analyticsLoading } = useGetAnalyticsTimeSeries(
        city,
        analyticsFilters,
    );

    const analyticsBuckets = useMemo(
        () =>
            (analyticsData?.buckets ?? []).map((bucket) => ({
                ...bucket,
                month: formatMonth(bucket.period) || bucket.period,
            })),
        [analyticsData],
    );
    const vendorBuckets = useMemo(
        () =>
            Object.entries(data?.mothly ?? {}).map(([period, value]) => ({
                month: formatMonth(period) || period,
                orderValue: value.total,
                shipping: value.shipping,
            })),
        [data],
    );

    return (
        <div className="home">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {loading ? (
                <div className="loadingSpinner center">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <header className="home__toolbar">
                        <div className="home__title-block">
                            <span className="home__eyebrow">لوحة المتابعة</span>
                            <h1>مرحبا بعودتك 🎉</h1>
                            <p>نظرة سريعة على نشاط المنصة والتحليلات الشهرية.</p>
                        </div>
                        {adminInfo?.super_admin ? (
                            <div className="home__city-filter">
                                <label htmlFor="home-city-filter">نطاق البيانات</label>
                                <Form.Select
                                    id="home-city-filter"
                                    onChange={(e) => setCity(e.target.value || null)}
                                    value={city || ""}
                                >
                                    <option value="">كل المدن</option>
                                    {cities?.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <span>يطبّق هذا النطاق على البطاقات والتحليلات أسفل الصفحة.</span>
                            </div>
                        ) : (
                            <div className="home__city-scope">
                                <span>نطاق البيانات</span>
                                <strong>{cityScopeLabel}</strong>
                            </div>
                        )}
                    </header>
                    <div className="statics">
                        <div className="home-section-heading">
                            <div>
                                <span className="home__eyebrow">ملخص سريع</span>
                                <h2>نظرة عامة</h2>
                            </div>
                            <span className="home-scope-chip">النطاق: {cityScopeLabel}</span>
                        </div>
                        {adminInfo?.role === "vendor" ? (
                            <div className="home-stats-grid">
                                <HomeStatCard
                                    tone="products"
                                    icon={<FontAwesomeIcon icon={faBurger} />}
                                    value={data?.products}
                                    label="منتج"
                                />
                                <HomeStatCard
                                    tone="orders"
                                    icon={<FontAwesomeIcon icon={faReceipt} />}
                                    value={data?.orders}
                                    label="كل الطلبات"
                                    hint="شاملة المحذوفة"
                                />
                                <HomeStatCard
                                    tone="money"
                                    icon={<FontAwesomeIcon icon={faMoneyBillTrendUp} />}
                                    value={data?.activeOrders}
                                    label="طلبات مستحقة"
                                    hint="نفس عدد صفحة الطلبات"
                                />
                                <HomeStatCard
                                    tone="deleted"
                                    icon={<FontAwesomeIcon icon={faTrash} />}
                                    value={data?.deletedOrders}
                                    label="طلبات محذوفة"
                                    hint="مُسوّاة في كشف حساب"
                                />
                            </div>
                        ) : (
                            <div className="home-stats-grid">
                                <HomeStatCard
                                    tone="customers"
                                    icon={<FontAwesomeIcon icon={faUser} />}
                                    value={data?.customers}
                                    label="زبون"
                                />
                                <HomeStatCard
                                    tone="vendors"
                                    icon={<FontAwesomeIcon icon={faUsers} />}
                                    value={data?.vendors}
                                    label="مطعم"
                                />
                                <HomeStatCard
                                    tone="areas"
                                    icon={<FontAwesomeIcon icon={faLocationDot} />}
                                    value={data?.areas}
                                    label="منطقة"
                                />
                                <HomeStatCard
                                    tone="products"
                                    icon={<FontAwesomeIcon icon={faBurger} />}
                                    value={data?.products}
                                    label="منتج"
                                />
                                <HomeStatCard
                                    tone="deliveries"
                                    icon={<FontAwesomeIcon icon={faTruckFast} />}
                                    value={data?.onlineDeliveries}
                                    label="سائق"
                                />
                                <HomeStatCard
                                    tone="orders"
                                    icon={<FontAwesomeIcon icon={faCartShopping} />}
                                    value={data?.orders}
                                    label="كل الطلبات"
                                    hint="شاملة المحذوفة"
                                />
                                <HomeStatCard
                                    tone="money"
                                    icon={<FontAwesomeIcon icon={faMoneyBillTrendUp} />}
                                    value={data?.activeOrders}
                                    label="طلبات مستحقة"
                                    hint="نفس عدد صفحة الطلبات"
                                />
                                <HomeStatCard
                                    tone="deleted"
                                    icon={<FontAwesomeIcon icon={faTrash} />}
                                    value={data?.deletedOrders}
                                    label="طلبات محذوفة"
                                    hint="مُسوّاة في كشف حساب"
                                />
                            </div>
                        )}
                    </div>
                    <div className="chart">
                        <h3>تحليلات الأداء</h3>
                        {adminInfo?.role === "admin" ? (
                            <div className="analytics-toolbar">
                                <div>
                                    <label htmlFor="analytics-period">الفترة</label>
                                    <Form.Select
                                        id="analytics-period"
                                        value={periodPreset}
                                        onChange={(event) => setPeriodPreset(event.target.value)}
                                    >
                                        <option value="3">آخر 3 أشهر حتى الآن</option>
                                        <option value="6">آخر 6 أشهر حتى الآن</option>
                                        <option value="12">آخر 12 شهراً حتى الآن</option>
                                        <option value="24">آخر 24 شهراً حتى الآن</option>
                                        <option value="custom">نطاق مخصص</option>
                                    </Form.Select>
                                </div>
                                <div>
                                    <label htmlFor="analytics-status">حالة الطلب</label>
                                    <Form.Select
                                        id="analytics-status"
                                        value={orderStatus}
                                        onChange={(event) => setOrderStatus(event.target.value)}
                                    >
                                        <option value="all">كل الحالات</option>
                                        {ORDER_STATUS_CHOICES.map((item) => (
                                            <option key={item.en} value={item.en}>
                                                {item.ar}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                                {periodPreset === "custom" ? (
                                    <>
                                        <div>
                                            <label htmlFor="analytics-start">من</label>
                                            <DateTimeField
                                                inputId="analytics-start"
                                                dateOnly
                                                edge="start"
                                                value={customStartDate}
                                                onChange={setCustomStartDate}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="analytics-end">إلى</label>
                                            <DateTimeField
                                                inputId="analytics-end"
                                                dateOnly
                                                edge="end"
                                                value={customEndDate}
                                                onChange={setCustomEndDate}
                                            />
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        ) : null}
                        {adminInfo?.role === "admin" && analyticsLoading ? (
                            <div className="loadingSpinner center analytics-loading">
                                <Ring size={32} lineWeight={4} speed={2} color={BRAND} />
                            </div>
                        ) : adminInfo?.role !== "admin" ? (
                            <div className="analytics-chart-grid">
                                <div className="analytics-chart-card analytics-chart-card--wide">
                                    <h4>الاحصائيات الشهريه</h4>
                                    <ChartComponent
                                        data={vendorBuckets}
                                        valueType="money"
                                        series={[
                                            {
                                                dataKey: "orderValue",
                                                label: "الإجمالي",
                                                color: "#8884d8",
                                            },
                                            {
                                                dataKey: "shipping",
                                                label: "الشحن",
                                                color: "#82ca9d",
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="analytics-chart-grid">
                                <div className="analytics-chart-card analytics-chart-card--wide">
                                    <h4>قيمة الطلبات</h4>
                                    <p className="chart-note">
                                        إجمالي الطلبات بدون الشحن، مع إجمالي الشحن بشكل منفصل
                                    </p>
                                    <ChartComponent
                                        data={analyticsBuckets}
                                        valueType="money"
                                        series={[
                                            {
                                                dataKey: "orderValue",
                                                label: "الإجمالي",
                                                color: "#8884d8",
                                            },
                                            {
                                                dataKey: "shipping",
                                                label: "الشحن",
                                                color: "#82ca9d",
                                            },
                                        ]}
                                    />
                                </div>
                                <div className="analytics-chart-card">
                                    <h4>عدد الطلبات</h4>
                                    <p className="chart-note">عدد الطلبات التي أُنشئت في كل شهر</p>
                                    <ChartComponent
                                        data={analyticsBuckets}
                                        series={[
                                            {
                                                dataKey: "orders",
                                                label: "الطلبات",
                                                color: "#f28c28",
                                            },
                                        ]}
                                    />
                                </div>
                                <div className="analytics-chart-card">
                                    <h4>الحسابات الجديدة</h4>
                                    <p className="chart-note">
                                        الزبائن والمطاعم والسائقون الذين سُجلوا
                                    </p>
                                    <ChartComponent
                                        data={analyticsBuckets}
                                        series={[
                                            {
                                                dataKey: "customers",
                                                label: "الزبائن",
                                                color: "#4aa3df",
                                            },
                                            {
                                                dataKey: "vendors",
                                                label: "المطاعم",
                                                color: "#f28c28",
                                            },
                                            {
                                                dataKey: "drivers",
                                                label: "السائقون",
                                                color: "#82ca9d",
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        )}
                        {!analyticsLoading && analyticsBuckets.length === 0 ? (
                            <p className="chart-note">لا توجد بيانات في الفترة المحددة</p>
                        ) : null}
                        <div className="chartContainer">
                            <span className="home-hint">يتم تحديث التحليلات تلقائياً كل دقيقة</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
export default Home;
